import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom";
import { flujoCompraTerminado, terminarFlujoCompra, marcarFinFlujoCompra, flujoCompraReanudado, guardarBorradorModificar, borrarBorradorModificar, leerBorradorModificar } from "../../utils/flujoCompra";
import axios from "axios";
import { MENSAJE_ERROR_SESION } from "../../api/setupAxios";

import { hoyMadrid } from "../../utils/fecha";
const todayISO = hoyMadrid; // fecha de Madrid (antes UTC: de 00:00 a 02:00 contaba como ayer)

// Helper de almacenamiento: usa localStorage (preferido) y espejo en sessionStorage
const storage = {
  getItem: (k) => {
    try {
      const v = localStorage.getItem(k);
      return v != null ? v : sessionStorage.getItem(k);
    } catch {
      return sessionStorage.getItem(k);
    }
  },
  setItem: (k, v) => {
    try { localStorage.setItem(k, v); } catch {}
    try { sessionStorage.setItem(k, v); } catch {}
  },
  removeItem: (k) => {
    try { localStorage.removeItem(k); } catch {}
    try { sessionStorage.removeItem(k); } catch {}
  },
};

// Compra NUEVA de un piso que ya tiene un pedido en estado "pedido": no se deja
// seguir (se vuelve a la lista de pisos, que ya no lo muestra), se llegue desde
// donde se llegue (atrás, recarga, otro móvil…). Lo comprueba el servidor.
const avisoPedidoPendiente = (abreviatura, d) =>
  `${abreviatura ? `El piso ${abreviatura}` : "Este piso"} ya tiene un pedido pendiente (compra #${d.id}` +
  `${d.precio_total != null ? `, ${Number(d.precio_total).toFixed(2).replace(".", ",")} €` : ""}). ` +
  "Para cambiarlo usa «Modificar compras».";

// Lo que se cambia en el carrito (cantidades, notas, alumnos) se apunta también en la
// entrada del historial con la que se entró (location.state). Al recargar, el carrito
// se monta con ese state: sin esto volvía a lo que traía de Crear compras y se perdían
// los cambios hechos en el propio carrito. No navega ni re-renderiza nada.
const apuntarEnHistorial = (cambios) => {
  try {
    const h = window.history.state;
    if (!h || !h.usr || typeof h.usr !== "object") return;
    window.history.replaceState({ ...h, usr: { ...h.usr, ...cambios } }, "");
  } catch (_) { /* sin historial: el borrador guardado sigue sirviendo */ }
};

const Carrito = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id_piso: id_piso_param } = useParams();
  const initialForceStudentPrompt = Boolean(location.state?.desdeModificar);

  const [notas_s, setNotasSupervisora] = useState("");
  const [numEstudiantes, setNumEstudiantes] = useState("");
  const [showEstudiantesModal, setShowEstudiantesModal] = useState(false);
  const [tempEstudiantes, setTempEstudiantes] = useState("");
  const [modalError, setModalError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showExitoModal, setShowExitoModal] = useState(false);
  // Texto del modal de éxito cuando el servidor ha actualizado un pedido
  // pendiente ya existente en vez de crear uno nuevo (reenvío del carrito).
  const [mensajeExito, setMensajeExito] = useState("");
  const [requireStudentReentry, setRequireStudentReentry] = useState(false);
  const [forceStudentPrompt, setForceStudentPrompt] = useState(initialForceStudentPrompt);
  const confirmDataRef = React.useRef(null);
  const [enviando, setEnviando] = useState(false); // evita doble envío
  const [productos, setProductos] = useState([]);
  const [abreviatura, setAbreviatura] = useState("");
  const [id_piso, setIdPiso] = useState("");
  const [user_supervisora, setUserSupervisora] = useState("");
  // Layout responsivo para los botones fijos (evita solapamientos en móviles)
  const [buttonLayout, setButtonLayout] = useState('sideBySide'); // 'sideBySide' | 'sideBySideNarrow' | 'stack'
  // Evita re-escribir el storage en el unmount tras confirmar compra
  const skipPersistRef = React.useRef(false);
  const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const {
    id,
    productos: productosIniciales = [],
    id_piso: pisoInicial,
    abreviatura_piso,
    user_supervisora: supervisoraInicial,
    notas_supervisora,
    n_estudiantes: nEstudiantesInicial,
    accessToken,
    desdeModificar,
  } = location.state || {};

  // --- CONTROL TOKEN / CONFIRMACIÓN ---
  // accessToken procede de CrearCompras (state). Aquí validamos:
  //  * Si la compra ya está confirmada (compraConfirmada_<id_piso> = '1') y el usuario regresa vía back_forward sin token válido => redirigir.
  //  * Tras confirmar: marcamos flag y borramos token para invalidar la sesión de creación.
  // Esto bloquea re-entradas fortuitas con el botón atrás evitando duplicados o modificaciones inesperadas.

  // Protección "atrás" tras confirmar (ver CrearCompras.js): si se llega al
  // carrito con atrás/adelante (POP) y este piso ya se confirmó, fuera.
  const tipoNavegacion = useNavigationType();
  const finAlEntrar = React.useRef(tipoNavegacion === 'POP' && flujoCompraTerminado());
  useEffect(() => {
    if (finAlEntrar.current) terminarFlujoCompra(navigate); // atrás tras confirmar → como un árbol
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const piso = pisoInicial || location.state?.id_piso || id_piso_param;
    if (finAlEntrar.current || flujoCompraReanudado() || !piso || desdeModificar || tipoNavegacion !== 'POP') return;
    let confirmada = false;
    try { confirmada = sessionStorage.getItem(`compraConfirmada_${piso}`) === '1'; } catch (_) {}
    if (confirmada) {
      navigate('/supervisora/pisoCrearCompras', { replace: true, state: { aviso: 'Ese pedido ya está confirmado. Para cambiarlo usa «Modificar compras».' } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const piso = pisoInicial || location.state?.id_piso || id_piso_param;
    if (finAlEntrar.current || flujoCompraReanudado() || !piso) return undefined;
    let vivo = true;
    (async () => {
      // 1º: ¿quedó una confirmación a medias? (si se guardó, panel verde y nada más)
      if (await comprobarPendienteAlEntrar(() => vivo)) return;
      if (!vivo || id || desdeModificar) return; // modificar una compra: lo de abajo no aplica
      // 2º: compra nueva de un piso que ya tiene pedido pendiente → fuera
      try {
        const r = await axios.get(`/api/carrito/pedido_pendiente/${piso}`);
        if (vivo && r.data?.pendiente) {
          navigate('/supervisora/pisoCrearCompras', { replace: true, state: { aviso: avisoPedidoPendiente(abreviatura || abreviatura_piso, r.data) } });
        }
      } catch (_) { /* sin conexión: se deja seguir; el servidor vuelve a comprobarlo al confirmar */ }
    })();
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Token keys / confirm flag
  const tokenKeyRef = React.useRef(null);
  const confirmKeyRef = React.useRef(null);
  useEffect(() => {
    const piso = pisoInicial || location.state?.id_piso || id_piso || id_piso_param;
    if (!piso) return;
    tokenKeyRef.current = `crearCompras_token_${piso}`;
    confirmKeyRef.current = `compraConfirmada_${piso}`;
    // Validación de token: si existe confirmación previa y el token usado no coincide -> bloquear
    const navType = performance.getEntriesByType('navigation')[0]?.type;
    const confirmada = sessionStorage.getItem(confirmKeyRef.current) === '1';
    const storedToken = tokenKeyRef.current ? sessionStorage.getItem(tokenKeyRef.current) : null;
    if (confirmada && navType === 'back_forward') {
      if (!accessToken || !storedToken || storedToken !== accessToken) {
        navigate('/supervisora/pisoCrearCompras', { replace: true, state: { aviso: 'Compra confirmada: no se puede reabrir con atrás.' } });
      }
    }
  }, [pisoInicial, id_piso, accessToken, navigate]);

  // clave estable para storage (se calcula en mount y no cambia)
  // id_piso_param (URL) garantiza que sobrevive refresh / iOS PWA suspend
  const tocadoCarritoRef = React.useRef(false); // ¿ha cambiado algo en el carrito? (borrador de modificar)
  const storageBaseRef = React.useRef(pisoInicial || (location.state && location.state.id_piso) || id_piso_param || "");
  const getStorageKey = () => (storageBaseRef.current ? `compraData_piso_${storageBaseRef.current}` : null);

  // Guardar en la clave principal y en claves alternativas que podría usar crearCompras.
  // Siempre se sella con la fecha de HOY: CrearCompras.js descarta como "compra
  // anterior" cualquier borrador cuya fecha no sea la de hoy, así que si aquí no
  // se estampara también, cualquier guardado desde el carrito borraría esa fecha
  // y el propio carrito de hoy se vería (incorrectamente) como caducado.
  const saveToAllKeys = (data) => {
    try {
      const payload = JSON.stringify({ ...data, fecha: todayISO() });
      // clave principal
      const keyMain = getStorageKey();
      if (keyMain) storage.setItem(keyMain, payload);

      // clave según pisoInicial (por compatibilidad)
      if (pisoInicial) {
        storage.setItem(`compraData_piso_${pisoInicial}`, payload);
      }

      // clave según estado actual id_piso si está disponible
      if (id_piso) {
        storage.setItem(`compraData_piso_${id_piso}`, payload);
      }

      // clave genérica reciente (fallback)
      storage.setItem(`compraData_latest`, payload);
    } catch (e) {
      console.error("Error guardando en varias claves de storage:", e);
    }
  };

  // Guardar datos en storage cuando cambien
  useEffect(() => {
    if (desdeModificar) {
      // Modificar: los cambios sin confirmar quedan en el borrador (solo si se ha tocado
      // algo aquí o ya había cambios pendientes; ver flujoCompra.js)
      if (productos.length > 0 && (tocadoCarritoRef.current || leerBorradorModificar(id))) {
        guardarBorradorModificar(id, { productos: productos.filter((p) => p.cantidad > 0), notas_supervisora: notas_s, id_piso: pisoInicial || id_piso_param, abreviatura: abreviatura || abreviatura_piso });
      }
      return;
    }
    if (productos.length > 0 || notas_s.trim() || abreviatura || numEstudiantes !== "") {
      const data = {
        id,
        productos: productos.filter((p) => p.cantidad > 0),
        id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
        abreviatura_piso: abreviatura || abreviatura_piso,
        user_supervisora,
        notas_supervisora: notas_s,
        n_estudiantes: toIntOrNull(numEstudiantes),
      };
      saveToAllKeys(data);
    }
  }, [productos, id, id_piso, abreviatura, abreviatura_piso, user_supervisora, notas_s, numEstudiantes, desdeModificar]);

  // Restaurar datos si se recarga o se entra directo
  useEffect(() => {
    // Piso y supervisora SIEMPRE desde el state (también si la compra está vacía, p. ej.
    // al modificar una compra de 0 €: antes se quedaban sin cargar y no dejaba confirmar)
    if (location.state) {
      if (pisoInicial) setIdPiso(pisoInicial);
      if (abreviatura_piso || location.state.abreviatura) setAbreviatura(abreviatura_piso || location.state.abreviatura);
      if (supervisoraInicial) setUserSupervisora(supervisoraInicial);
    }
    // Si venimos con state desde crearCompras: usar esos datos y guardarlos inmediatamente
    if (location.state && productosIniciales.length > 0) {
      setProductos(productosIniciales);
      setAbreviatura(abreviatura_piso || "");
      setIdPiso(pisoInicial || "");
      setUserSupervisora(supervisoraInicial || "");
      if (nEstudiantesInicial != null) {
        setNumEstudiantes(String(nEstudiantesInicial));
      }
      if (notas_supervisora?.trim()) setNotasSupervisora(notas_supervisora);

      // fijar la base de storage y guardar el objeto recibido para que crearCompras
      // encuentre los cambios aunque el usuario use el botón atrás del navegador
      if (!storageBaseRef.current && (pisoInicial || (location.state && location.state.id_piso))) {
        storageBaseRef.current = pisoInicial || (location.state && location.state.id_piso);
      }
      if (!desdeModificar) {
        try {
          const initialData = {
            id,
            productos: productosIniciales.filter((p) => p.cantidad > 0),
            id_piso: storageBaseRef.current || pisoInicial || "",
            abreviatura_piso: abreviatura_piso || abreviatura || "",
            user_supervisora: supervisoraInicial || user_supervisora || "",
            notas_supervisora: notas_supervisora || notas_s,
            n_estudiantes: toIntOrNull(nEstudiantesInicial ?? numEstudiantes),
          };
            saveToAllKeys(initialData);
        } catch (e) {
          console.error("Error guardando initial state en sessionStorage:", e);
        }
      }
      return;
    }

    // Intentar recuperar por la clave actual y, si no, por la clave inicial (por compatibilidad)
  if (desdeModificar) return; // no restaurar nada
  const keyCurrent = getStorageKey();
    const keyInitial = pisoInicial ? `compraData_piso_${pisoInicial}` : null;
    const contextPiso = storageBaseRef.current || pisoInicial || id_piso || id_piso_param || ""; // piso de contexto actual
    let guardado = null;

    try {
      if (keyCurrent) guardado = storage.getItem(keyCurrent);
      if (!guardado && keyInitial) guardado = storage.getItem(keyInitial);

      // Fallback: latest (validar id_piso antes de usarlo)
      if (!guardado) {
        const latestRaw = storage.getItem("compraData_latest");
        if (latestRaw) {
          const latestObj = JSON.parse(latestRaw);
          const savedPiso = latestObj?.id_piso ?? "";
          if (contextPiso && savedPiso && String(savedPiso) !== String(contextPiso)) {
            // Piso no coincide: ignorar latest y volver a la lista de pisos
            navigate("/supervisora/pisoCrearCompras", { replace: true });
            return;
          }
          guardado = latestRaw;
        }
      }

      // Un borrador de otro día es de una compra anterior ya abandonada, no la
      // sesión actual: se descarta (y se limpia) en vez de resucitarla sin más.
      if (guardado) {
        try {
          const fechaGuardado = JSON.parse(guardado).fecha;
          if (fechaGuardado !== todayISO()) {
            if (keyCurrent) storage.removeItem(keyCurrent);
            if (keyInitial) storage.removeItem(keyInitial);
            storage.removeItem("compraData_latest");
            guardado = null;
          }
        } catch (_) { /* si no parsea, sigue igual y fallará más abajo */ }
      }

      if (guardado) {
        const data = JSON.parse(guardado);
        setProductos(data.productos || []);
        setAbreviatura(data.abreviatura ?? data.abreviatura_piso ?? "");
        setIdPiso(data.id_piso || (pisoInicial || id_piso_param || ""));
        setUserSupervisora(data.user_supervisora || (supervisoraInicial || ""));
        setNotasSupervisora(data.notas_supervisora || "");
        setNumEstudiantes(
          data.n_estudiantes !== undefined && data.n_estudiantes !== null
            ? String(data.n_estudiantes)
            : ""
        );
        // asegurar que la clave estable quede fijada si antes estaba vacía
        if (!storageBaseRef.current && data.id_piso) {
          storageBaseRef.current = data.id_piso;
        }
      }
      // Si no hay ningún dato guardado y tenemos id_piso_param, redirigir al panel
      if (!guardado && id_piso_param && !pisoInicial && !flujoCompraReanudado()) {
        // a la lista de pisos; si el piso ya tiene pedido pendiente, con ese aviso
        const piso = id_piso_param;
        axios.get(`/api/carrito/pedido_pendiente/${piso}`)
          .then((r) => r.data?.pendiente ? avisoPedidoPendiente("", r.data) : null)
          .catch(() => null)
          .then((avisoPendiente) => navigate("/supervisora/pisoCrearCompras", {
            replace: true,
            state: { aviso: avisoPendiente || 'La sesión de compra expiró. Por favor, empieza de nuevo.' },
          }));
        return;
      }
    } catch (e) {
      console.error("Error leyendo storage:", e);
    }
  }, [location.state, productosIniciales, notas_supervisora, abreviatura_piso, pisoInicial, supervisoraInicial, desdeModificar]);
  
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (flujoCompraTerminado() || skipPersistRef.current) return; // compra ya confirmada: nada que perder
      if (productos.length > 0 || notas_s.trim() !== "" || numEstudiantes !== "") {
        e.preventDefault();
        e.returnValue = "⚠️ Si recargas o cierras la página, se perderán los datos ingresados.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [productos, notas_s, numEstudiantes]);
  // Manejo del botón atrás del navegador
  // ✅ Ya no bloqueamos el botón atrás ni usamos pushState
// ✅ Guardar el carrito al desmontar (sin pushState, sin bloquear atrás)
useEffect(() => {
  return () => {
    if (desdeModificar) return; // jamás persistir al salir
    if (skipPersistRef.current) return; // confirmación limpia
    const data = {
      id,
      productos: (productos || []).filter((p) => p.cantidad > 0),
      id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
      abreviatura_piso: abreviatura || abreviatura_piso,
      user_supervisora,
      notas_supervisora: notas_s,
      n_estudiantes: toIntOrNull(numEstudiantes),
    };
    saveToAllKeys(data);
  };
}, [id, productos, id_piso, abreviatura, abreviatura_piso, user_supervisora, notas_s, desdeModificar]);

  // Detectar ancho de ventana para evitar que los botones fijos se solapen
  useEffect(() => {
    function computeLayout() {
      const w = window.innerWidth;
      // Ajuste: solo apilar muy estrecho (p. ej. <360). Entre 360 y 700 usamos lado a lado estrecho
      if (w < 360) setButtonLayout('stack');
      else if (w < 700) setButtonLayout('sideBySideNarrow');
      else setButtonLayout('sideBySide');
    }

    computeLayout();
    window.addEventListener('resize', computeLayout);
    return () => window.removeEventListener('resize', computeLayout);
  }, []);

  const actualizarCantidad = (idx, nuevaCantidad) => {
    setProductos((prev) => {
      const copia = prev.map((p) => ({ ...p }));
      copia[idx].cantidad = Math.max(0, nuevaCantidad);
      const nueva = copia.filter((p) => p.cantidad > 0);
      apuntarEnHistorial({ productos: nueva });
      tocadoCarritoRef.current = true;
      if (desdeModificar) {
        // (también si se queda vacío: el efecto de arriba no guarda listas vacías)
        guardarBorradorModificar(id, { productos: nueva, notas_supervisora: notas_s, id_piso: pisoInicial || id_piso_param, abreviatura: abreviatura || abreviatura_piso });
      }
      if (!desdeModificar) {
        try {
          const data = {
            id,
            productos: nueva, // ya sin items con cantidad 0
            id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
            abreviatura_piso: abreviatura || abreviatura_piso,
            user_supervisora,
            notas_supervisora: notas_s,
            n_estudiantes: toIntOrNull(numEstudiantes),
          };
          saveToAllKeys(data); // guarda en localStorage + sessionStorage
        } catch (e) {
          console.error("Error guardando en actualizarCantidad:", e);
        }
      }
      return nueva;
    });
  };

  const precio_total = productos.reduce(
    (total, p) => total + p.precio * p.cantidad,
    0
  );

  // Compra guardada (respuesta del servidor): limpiar borradores y mostrar el panel verde.
  const trasConfirmar = (resData) => {
        // OPTION C: Limpieza TOTAL de cualquier rastro de la compra en storage.
    // Objetivo: que el botón atrás no rehidrate nada y quede bloqueado por el flag de confirmación.
    // 1. Marcamos confirmación y eliminamos el token para invalidar la sesión de creación.
    try {
      if (confirmKeyRef.current) sessionStorage.setItem(confirmKeyRef.current, '1');
    } catch (e) { console.warn('No se pudo marcar confirmación', e); }
    try {
      if (tokenKeyRef.current) sessionStorage.removeItem(tokenKeyRef.current);
    } catch (e) { console.warn('No se pudo eliminar token', e); }

    // 2. Eliminar TODAS las claves compraData_* relacionadas con el piso en ambos storages.
    const pisoContext = storageBaseRef.current || id_piso || pisoInicial || '';
    const borrarClavesCompra = (area) => {
      try {
        const keys = [];
        for (let i = 0; i < area.length; i++) {
          const k = area.key(i);
          if (!k) continue;
          if (k === 'compraData_latest' || (pisoContext && k.startsWith(`compraData_piso_${pisoContext}`))) {
            keys.push(k);
          }
        }
        keys.forEach(k => { try { area.removeItem(k); } catch {} });
      } catch (e) { /* ignore */ }
    };
    try { borrarClavesCompra(localStorage); } catch {}
    try { borrarClavesCompra(sessionStorage); } catch {}

    // 3. Asegurar que no se vuelque nada en el unmount.
    skipPersistRef.current = true;
    // a dónde se vuelve al salir (y "atrás" ya no regresa a crear compras / carrito)
    marcarFinFlujoCompra("/supervisora/compras", null); // siempre a Compras
    borrarBorradorModificar(id);

    // 4. Mostrar modal de éxito (navega al confirmar).
    setMensajeExito(
      resData.operation === "update_auto"
        ? `Ya tenías un pedido pendiente para este piso (compra #${resData.id}): se ha actualizado con lo que tenías en este carrito.`
        : ""
    );
    setShowExitoModal(true);
    setEnviando(false);
  };

  // ── Confirmación con código único (id_envio, ver carrito.py) ─────────────────
  // Con mala cobertura la compra puede guardarse y la respuesta no llegar: en vez de
  // quedarse colgada, la app pregunta al servidor por el código hasta saber si se
  // guardó (y si no, lo reenvía con el mismo código: nunca se guarda dos veces).
  const clavePendiente = () => `envioCompra_${storageBaseRef.current || id_piso || pisoInicial || id_piso_param || ""}`;
  const leerPendiente = () => { try { return JSON.parse(localStorage.getItem(clavePendiente()) || "null"); } catch { return null; } };
  const guardarPendiente = (v) => {
    try { if (v) localStorage.setItem(clavePendiente(), JSON.stringify(v)); else localStorage.removeItem(clavePendiente()); } catch { /* sin storage */ }
  };
  const nuevoIdEnvio = () => (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
  const firmaContenido = (prods, notas) => JSON.stringify({ p: (prods || []).filter((p) => p.cantidad > 0).map((p) => [p.id, p.cantidad]), n: notas || "" });
  const [comprobandoEnvio, setComprobandoEnvio] = useState(false);
  const comprobacionRef = React.useRef(null);
  const contenidoRef = React.useRef({ productos: [], notas: "" });
  contenidoRef.current = { productos, notas: notas_s };

  const iniciarComprobacion = (payload, envio) => {
    if (comprobacionRef.current) comprobacionRef.current.fin();
    const ctrl = { cancelado: false, reloj: null };
    const alVolverLaRed = () => { clearTimeout(ctrl.reloj); paso(); };
    ctrl.fin = () => {
      ctrl.cancelado = true;
      clearTimeout(ctrl.reloj);
      window.removeEventListener("online", alVolverLaRed);
      if (comprobacionRef.current === ctrl) comprobacionRef.current = null;
      setComprobandoEnvio(false);
    };
    const paso = async () => {
      if (ctrl.cancelado) return;
      try {
        const { data } = await axios.get(`/api/carrito/envio/${envio.id}`, { _connRetried: true, timeout: 10000 });
        if (ctrl.cancelado) return;
        if (data.estado === "ok") { ctrl.fin(); guardarPendiente(null); trasConfirmar(data.respuesta || { success: true }); return; }
        if (data.estado === "no") {
          // no llegó a guardarse: se reenvía con el mismo código
          try {
            const res = await axios.post(`/api/carrito/confirmar_compra`, { ...payload, id_envio: envio.id }, { _connRetried: true });
            if (ctrl.cancelado) return;
            if (res.data.success) { ctrl.fin(); guardarPendiente(null); trasConfirmar(res.data); return; }
          } catch (e2) {
            const r2 = e2 && e2.response;
            if (r2 && !(r2.status === 409 && r2.data?.codigo === "envio_en_proceso")) {
              // el servidor ha contestado con un error de verdad: se para y se avisa
              ctrl.fin(); guardarPendiente(null);
              alert("❌ Error al registrar la compra" + (r2.data?.error ? `: ${r2.data.error}` : ""));
              return;
            }
          }
        }
      } catch (_) { /* sin conexión todavía: se sigue intentando */ }
      if (!ctrl.cancelado) ctrl.reloj = setTimeout(paso, 4000);
    };
    window.addEventListener("online", alVolverLaRed);
    comprobacionRef.current = ctrl;
    setComprobandoEnvio(true);
    ctrl.reloj = setTimeout(paso, 2000);
  };
  useEffect(() => () => { if (comprobacionRef.current) comprobacionRef.current.fin(); }, []);

  // Al entrar al carrito: si quedó una confirmación sin saber cómo acabó (se cerró la
  // app esperando), se pregunta al servidor ANTES que nada. Devuelve true si la compra
  // ya estaba guardada (y se muestra el panel verde).
  const comprobarPendienteAlEntrar = async (sigueVivo) => {
    const envio = leerPendiente();
    if (!envio || !envio.id) return false;
    try {
      const { data } = await axios.get(`/api/carrito/envio/${envio.id}`, { _connRetried: true, timeout: 10000 });
      if (!sigueVivo()) return true;
      if (data.estado === "ok") {
        guardarPendiente(null);
        const c = contenidoRef.current;
        if (firmaContenido(c.productos, c.notas) === envio.fc) {
          trasConfirmar(data.respuesta || { success: true });
          return true;
        }
        alert(`ℹ️ Esta compra ya se había guardado (compra #${data.respuesta?.id ?? ""}) con lo que había entonces en el carrito. Para cambiarla usa «Modificar compras».`);
      } else if (data.estado === "no") {
        guardarPendiente(null);
      }
    } catch (_) { /* sin conexión: se mirará la próxima vez */ }
    return false;
  };

   const ejecutarConfirmacion = async (estudiantesInt, { forzarNueva = false } = {}) => {
    setEnviando(true);
    let compraAvanzada = null;
    let envioEnCurso = null;
    try {
      const precio_total_fixed = Number(precio_total.toFixed(2));

      // Fallbacks defensivos: si el state local quedó vacío tras un re-entry
      // (botón atrás, recarga, navegación cruzada) intentamos recuperar los
      // identificadores desde el ref estable, el state inicial de location y,
      // como último recurso, desde la clave guardada en storage.
      let idPisoFinal = id_piso || storageBaseRef.current || pisoInicial || "";
      let abrevFinal = abreviatura_piso || abreviatura || "";
      let userSupFinal = user_supervisora || supervisoraInicial || "";
      if (!idPisoFinal || !abrevFinal || !userSupFinal) {
        try {
          const keyMain = getStorageKey();
          const raw =
            (keyMain && storage.getItem(keyMain)) ||
            storage.getItem("compraData_latest");
          if (raw) {
            const saved = JSON.parse(raw);
            if (!idPisoFinal && saved?.id_piso) idPisoFinal = saved.id_piso;
            if (!abrevFinal && (saved?.abreviatura_piso || saved?.abreviatura)) {
              abrevFinal = saved.abreviatura_piso || saved.abreviatura;
            }
            if (!userSupFinal && saved?.user_supervisora) {
              userSupFinal = saved.user_supervisora;
            }
          }
        } catch (e) {
          console.warn("No se pudo rehidratar desde storage al confirmar:", e);
        }
      }

      // Abreviatura y supervisora: si siguen sin estar (p. ej. al modificar una compra
      // vacía no se cargaban), se completan aquí y, si aun así faltan, las pone el
      // servidor a partir del piso. Solo el piso es imprescindible.
      if (!idPisoFinal) idPisoFinal = id_piso_param || "";
      if (!abrevFinal) abrevFinal = location.state?.abreviatura || location.state?.abreviatura_piso || "";
      if (!userSupFinal) {
        try {
          const yo = JSON.parse(localStorage.getItem("user") || "{}");
          if (yo && yo.tipo === "supervisora") userSupFinal = yo.user || "";
        } catch (_) { /* el servidor la completa */ }
      }

      if (!idPisoFinal) {
        console.error("Datos del piso incompletos al confirmar:", {
          idPisoFinal, abrevFinal, userSupFinal,
        });
        alert(
          "❌ No se pudo registrar la compra: faltan datos del piso. " +
          "Vuelve al panel y entra de nuevo al carrito."
        );
        setEnviando(false);
        return;
      }

      const payload = {
        id: forzarNueva ? null : id,
        forzar_nueva: forzarNueva,
        id_piso: idPisoFinal,
        abreviatura_piso: abrevFinal,
        user_supervisora: userSupFinal,
        productos,
        precio_total: precio_total_fixed,
        notas_supervisora: notas_s,
        n_estudiantes: estudiantesInt,
      };
      // mismo contenido = mismo código (si se pulsa otra vez tras un corte, no duplica)
      const firma = JSON.stringify({ c: firmaContenido(productos, notas_s), e: estudiantesInt, f: !!forzarNueva, id: payload.id || null });
      let envio = leerPendiente();
      if (!envio || envio.firma !== firma) envio = { id: nuevoIdEnvio(), firma, fc: firmaContenido(productos, notas_s), ts: Date.now() };
      guardarPendiente(envio);
      envioEnCurso = { payload, envio };
      const res = await axios.post(`/api/carrito/confirmar_compra`, { ...payload, id_envio: envio.id }, { _connRetried: true });

      if (res.data.success) {
        guardarPendiente(null);
        trasConfirmar(res.data);
      } else {
        alert('❌ Error al guardar la compra');
      }
    } catch (error) {
      const backendMsg = error?.response?.data?.error;
      const status = error?.response?.status;
      console.error("❌ Error al confirmar compra:", status, backendMsg, error);
      const sinRespuesta = !error?.response || (status === 409 && error?.response?.data?.codigo === "envio_en_proceso");
      if (sinRespuesta && envioEnCurso) {
        // no sabemos si se ha guardado: preguntar al servidor hasta saberlo
        iniciarComprobacion(envioEnCurso.payload, envioEnCurso.envio);
      } else if (error?.response && status !== 401) {
        guardarPendiente(null); // el servidor contestó (no se guardó): el próximo intento, código nuevo
      }
      if (sinRespuesta && envioEnCurso) {
        // (aviso en pantalla: modal "Comprobando…")
      } else if (status === 401) {
        alert(MENSAJE_ERROR_SESION);
      } else if (status === 409 && error?.response?.data?.codigo === "compra_avanzada" && !forzarNueva) {
        compraAvanzada = error.response.data;
      } else {
        alert(
          "❌ Error al registrar la compra" +
          (backendMsg ? `: ${backendMsg}` : "")
        );
      }
    } finally {
      setEnviando(false);
    }

    // La compra de este piso ya la ha avanzado el comprador (no se puede
    // modificar): se ofrece crear otra compra con este mismo carrito.
    if (compraAvanzada) {
      const crearOtra = window.confirm(
        `La compra #${compraAvanzada.compra_id} de este piso ya está en estado "${compraAvanzada.estado}".\n\n` +
        "¿Quieres crear otra compra con este carrito?"
      );
      if (crearOtra) {
        await ejecutarConfirmacion(estudiantesInt, { forzarNueva: true });
      }
    }
  };

  const abrirConfirmacionCompra = (estudiantesInt) => {
    confirmDataRef.current = { estudiantesInt };
    setShowConfirmModal(true);
  };

  const confirmarCompra = () => {
    if (enviando) return; // doble click guardado
    const estudiantesInt = toIntOrNull(numEstudiantes);
    if (precio_total === 0) {
      abrirConfirmacionCompra(1);
      return;
    }
    if (forceStudentPrompt || requireStudentReentry || !estudiantesInt || estudiantesInt <= 0) {
      let initialValue = "";
      if (!requireStudentReentry && estudiantesInt && estudiantesInt > 0) {
        initialValue = String(estudiantesInt);
      } else if (!requireStudentReentry) {
        initialValue = numEstudiantes || "";
      }
      setTempEstudiantes(initialValue);
      setModalError("");
      setShowEstudiantesModal(true);
      return;
    }
    abrirConfirmacionCompra(estudiantesInt);
  };

  const cancelarModalEstudiantes = () => {
    setShowEstudiantesModal(false);
    setModalError("");
  };

  const confirmarModalEstudiantes = () => {
    const parsed = toIntOrNull(tempEstudiantes);
    if (!parsed || parsed <= 0) {
      setModalError("El número debe ser un entero positivo.");
      return;
    }
    const normalized = String(parsed);
    setNumEstudiantes(normalized);
    apuntarEnHistorial({ n_estudiantes: parsed });
    if (!desdeModificar) {
      try {
        const data = {
          id,
          productos: (productos || []).filter((p) => p.cantidad > 0),
          id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
          abreviatura_piso: abreviatura || abreviatura_piso,
          user_supervisora,
          notas_supervisora: notas_s,
          n_estudiantes: parsed,
        };
        saveToAllKeys(data);
      } catch (err) {
        console.error("Error guardando n_estudiantes desde modal:", err);
      }
    }
    setRequireStudentReentry(false);
    setForceStudentPrompt(false);
    setShowEstudiantesModal(false);
    setModalError("");
    abrirConfirmacionCompra(parsed);
  };

  const cancelarConfirmacionCompra = () => {
    setShowConfirmModal(false);
    confirmDataRef.current = null;
    setRequireStudentReentry(true);
  };

  const confirmarModalCompra = () => {
    const payload = confirmDataRef.current;
    if (!payload || !payload.estudiantesInt) {
      cancelarConfirmacionCompra();
      return;
    }
    setShowConfirmModal(false);
    ejecutarConfirmacion(payload.estudiantesInt);
  };

  const volverAtras = () => {
    const data = {
      id,
      productos: (productos || []).filter((p) => p.cantidad > 0),
      id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
      abreviatura: abreviatura || abreviatura_piso,
      user_supervisora,
      notas_supervisora: notas_s,
      n_estudiantes: toIntOrNull(numEstudiantes),
      desdeModificar,
    };
    if (!desdeModificar) {
      try { saveToAllKeys(data); } catch (e) { console.error("Error guardando antes de volver:", e); }
    }
    if (data.id_piso) navigate(`/supervisora/crearCompras/${data.id_piso}`, { state: data, replace: true });
    else navigate("/supervisora/compras", { replace: true });
  };

  const abreviaturaConfirm = abreviatura || abreviatura_piso || "este piso";
  const totalConfirm = `${precio_total.toFixed(2)} €`;
  const estudiantesConfirm = confirmDataRef.current?.estudiantesInt ?? toIntOrNull(numEstudiantes) ?? "-";

  return (
    <div
      style={{
        maxWidth: 700,
        margin: "0 auto",
        padding: 20,
        paddingBottom: 120, // espacio extra para los botones fijos
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#333",
        backgroundColor: "#fafafa",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: 20, color: "#0077cc" }}>
        Carrito de compra de <strong>{abreviatura}</strong>
      </h2>

      {productos.length === 0 ? (
        <p style={{ color: "red", fontWeight: "bold", textAlign: "center" }}>
          ⚠️ No hay productos en el carrito.
        </p>
      ) : (
  <ul style={{ listStyle: "none", padding: 0, overflowX: 'auto' }}>
          {productos.map((p, idx) => (
            <li
              key={idx}
              style={{
                marginBottom: 15,
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gridTemplateRows: "auto auto",
                gap: 6,
                padding: 12,
                borderRadius: 6,
                backgroundColor: "#fff",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              }}
            >
              {/* Row 1: name + unit price (left) and controls (right) */}
              <div style={{ gridColumn: '1 / span 1', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                <span title={p.nombre} style={{ fontWeight: 600, overflow: 'visible', textOverflow: 'unset', whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 0 }}>{p.nombre}</span>
                <span style={{ color: '#555', fontWeight: 500, whiteSpace: 'nowrap' }}>{p.precio.toFixed(2)}€ x ud</span>
              </div>

              <div style={{ gridColumn: '2 / span 1', display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => actualizarCantidad(idx, p.cantidad === 1 ? 0 : p.cantidad - 1)}
                  style={{
                    width: 36,
                    height: 32,
                    padding: '4px 8px',
                    fontSize: 14,
                    cursor: 'pointer',
                    backgroundColor: '#f8f8f8',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                  title={p.cantidad === 1 ? 'Eliminar producto' : 'Disminuir cantidad'}
                >
                  {p.cantidad === 1 ? '🗑️' : '-'}
                </button>

                <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{p.cantidad}</span>

                <button
                  onClick={() => actualizarCantidad(idx, p.cantidad + 1)}
                  style={{
                    width: 36,
                    height: 32,
                    padding: '4px 8px',
                    fontSize: 14,
                    cursor: 'pointer',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    backgroundColor: '#f8f8f8',
                    userSelect: 'none',
                    flexShrink: 0,
                  }}
                  aria-label={`Aumentar cantidad de ${p.nombre}`}
                >
                  +
                </button>
              </div>

              {/* Row 2: total, right-aligned */}
              <div style={{ gridColumn: '1 / span 2', display: 'flex', justifyContent: 'flex-end' }}>
                <span style={{ fontWeight: 700, color: '#0077cc' }}>{(p.cantidad * p.precio).toFixed(2)}€</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ textAlign: "right", marginTop: 30, color: "#0077cc" }}>
        Total: {precio_total.toFixed(2)} €
      </h3>

      <div style={{ marginTop: 30 }}>
        <label
          htmlFor="notas_supervisora" // alineado con el id del textarea
          style={{ fontWeight: "600", display: "block", marginBottom: 8 }}
        >
          Notas (opcional):
        </label>
        <textarea
          id="notas_supervisora"
          value={notas_s}
          onChange={(e) => {
            const nuevo = e.target.value;
            setNotasSupervisora(nuevo);
            apuntarEnHistorial({ notas_supervisora: nuevo });
            tocadoCarritoRef.current = true;
            try {
              const data = {
                id,
                productos: productos.filter((p) => p.cantidad > 0),
                id_piso: storageBaseRef.current || id_piso || pisoInicial || "",
                abreviatura_piso: abreviatura || abreviatura_piso,
                user_supervisora,
                notas_supervisora: nuevo, // se persiste al escribir
                n_estudiantes: toIntOrNull(numEstudiantes),
              };
              saveToAllKeys(data); // guarda en localStorage + sessionStorage
            } catch (e) {
              console.error("Error guardando notas:", e);
            }
          }}
          rows={4}
          placeholder="Escribe aquí cualquier observación adicional..."
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            fontSize: "14px",
            resize: "vertical",
          }}
        />
      </div>

      {showEstudiantesModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            zIndex: 10000,
            padding: "40px 16px 16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: 24,
              width: "100%",
              maxWidth: 360,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, color: "#0077cc" }}>
              ¿Para cuántos estudiantes es esta compra?
            </h3>
            <p style={{ marginTop: 0, marginBottom: 12, color: "#333" }}>
              Ingresa un número entero positivo para continuar.
            </p>
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              pattern="[0-9]*"
              value={tempEstudiantes}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "" || /^\d+$/.test(raw)) {
                  setTempEstudiantes(raw);
                  setModalError("");
                }
              }}
              autoFocus
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: 16,
                marginBottom: 8,
                appearance: "textfield",
              }}
            />
            {modalError && (
              <p style={{ color: "#c0392b", marginTop: 0, marginBottom: 12 }}>
                {modalError}
              </p>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 16,
              }}
            >
              <button
                onClick={cancelarModalEstudiantes}
                style={{
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarModalEstudiantes}
                style={{
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#0077cc",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {comprobandoEnvio && (
        <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 10060, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "22px 20px", maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 16px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 40 }}>📶</div>
            <h3 style={{ margin: "8px 0 10px", color: "#1e293b" }}>Comprobando si tu compra se ha guardado…</h3>
            <p style={{ color: "#475569", lineHeight: 1.5, margin: "0 0 16px", fontSize: 15.5 }}>
              La conexión es inestable. <b>No cierres la app</b>: en cuanto haya conexión sabremos si se ha guardado (y si no, se enviará sola). No se guardará dos veces.
            </p>
            <button
              onClick={() => comprobacionRef.current && comprobacionRef.current.fin()}
              style={{ border: "1px solid #cbd5e1", background: "#f8fafc", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: "pointer", color: "#334155" }}
            >
              Dejar de esperar (lo comprobaré al volver al carrito)
            </button>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            zIndex: 10050,
            padding: "48px 16px 16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: 24,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, color: productos.some((p) => p.cantidad > 0) ? "#0077cc" : "#b91c1c" }}>
              Confirmar compra
            </h3>
            {!productos.some((p) => p.cantidad > 0) && (
              <div style={{ background: "#fee2e2", border: "2px solid #dc2626", color: "#991b1b", borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontWeight: 800, fontSize: 16, lineHeight: 1.45 }}>
                ⚠️ Este pedido NO tiene productos.
                <div style={{ fontWeight: 600, fontSize: 14.5, marginTop: 4 }}>
                  Se guardará una compra vacía. Si tenías productos en el carrito y han desaparecido, pulsa «Cancelar» y vuelve a añadirlos.
                </div>
              </div>
            )}
            <p style={{ lineHeight: 1.5, color: "#333", fontSize: 16 }}>
              ¿Desea confirmar la compra para{' '}
              <span style={{ color: "#d35400", fontWeight: 700 }}>
                {abreviaturaConfirm}
              </span>{' '}
              de{' '}
              <span style={{ color: "#16a085", fontWeight: 700 }}>
                {totalConfirm}
              </span>
              {precio_total > 0 && (
                <>
                  {' '}para{' '}
                  <span style={{ color: "#8e44ad", fontWeight: 700 }}>
                    {estudiantesConfirm}
                  </span>{' '}
                  estudiantes
                </>
              )}
              ?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 24,
              }}
            >
              <button
                onClick={cancelarConfirmacionCompra}
                style={{
                  padding: "12px 18px",
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarModalCompra}
                style={{
                  padding: "12px 18px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: productos.some((p) => p.cantidad > 0) ? "#28a745" : "#dc2626",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {productos.some((p) => p.cantidad > 0) ? "Sí, confirmar" : "Sí, guardar vacía"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenedor fijo inferior que evita solapamientos responsivos */}
      <div
        aria-hidden={false}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'calc(12px + var(--fichaje-h, 0px))',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none', // los botones dentro controlan sus propios eventos
          zIndex: 9999,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 960,
            padding: '0 12px',
            display: 'flex',
            gap: buttonLayout === 'stack' ? 8 : (buttonLayout === 'sideBySideNarrow' ? 8 : 24),
            justifyContent: 'space-between',
            pointerEvents: 'auto', // re-enable for children
            alignItems: 'center',
            flexDirection: buttonLayout === 'stack' ? 'column' : 'row',
            alignContent: 'center',
          }}
        >
          <div style={{ flex: buttonLayout === 'stack' ? '0 0 auto' : '1 1 0', display: 'flex', justifyContent: 'flex-start' }}>
            <button
              onClick={volverAtras}
              style={{
                padding: buttonLayout === 'stack' ? '12px 16px' : '14px 18px',
                borderRadius: 8,
                backgroundColor: '#e46d6dff',
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: buttonLayout === 'stack' ? 16 : 16,
                minWidth: 110,
                flexShrink: 1,
                boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
              }}
            >
              ← Atrás
            </button>
          </div>

          <div style={{ flex: buttonLayout === 'stack' ? '0 0 auto' : '1 1 0', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={confirmarCompra}
              style={{
                padding: buttonLayout === 'stack' ? '12px 16px' : '14px 18px',
                borderRadius: 8,
                backgroundColor: '#0077cc',
                color: '#fff',
                fontWeight: 700,
                border: 'none',
                cursor: enviando ? 'default' : 'pointer',
                fontSize: buttonLayout === 'stack' ? 16 : 16,
                minWidth: 140,
                flexShrink: 0, // preferimos que este botón no se reduzca por debajo del minWidth
                boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
                opacity: enviando ? 0.85 : 1,
              }}
              disabled={enviando}
            >
              {enviando ? 'Guardando...' : 'Confirmar compra'}
            </button>
          </div>
        </div>
      </div>
      {showExitoModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'linear-gradient(160deg, #16a34a 0%, #15803d 40%, #065f46 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '24px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 420, width: '100%' }}>
            <div style={{ fontSize: 100, lineHeight: 1, marginBottom: 24, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))' }}>🎉</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: '#fff', marginBottom: 10, letterSpacing: '-0.5px', textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>¡Compra confirmada!</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.95)', marginBottom: 12 }}>
              {abreviatura || abreviatura_piso ? `Piso ${abreviatura || abreviatura_piso}` : 'Pedido registrado'} ✅
            </div>
            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.75)', marginBottom: 44, lineHeight: 1.6 }}>
              {mensajeExito || "La compra ha quedado registrada correctamente."}
            </div>
            <button
              style={{
                width: '100%', padding: '22px', borderRadius: 20,
                border: '3px solid rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.2)', color: '#fff',
                fontSize: 20, fontWeight: 900, cursor: 'pointer',
                backdropFilter: 'blur(8px)', letterSpacing: '0.3px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
              onClick={() => terminarFlujoCompra(navigate)}
            >
              Volver a mis compras
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carrito;
